import React, { useEffect, useState } from "react";
import {
    tokenUsageTracker,
    TokenUsageData,
} from "../services/tokenUsageTracker";

interface TokenUsageMeterProps {
    className?: string;
    showDetails?: boolean;
}

export const TokenUsageMeter: React.FC<TokenUsageMeterProps> = ({
    className = "",
    showDetails = true,
}) => {
    const [usageData, setUsageData] = useState<TokenUsageData | null>(
        tokenUsageTracker.getUsageData()
    );

    useEffect(() => {
        // Update state when usage data changes
        const handleUsageUpdate = (data: TokenUsageData) => {
            setUsageData(data);
        };

        tokenUsageTracker.on("usageUpdated", handleUsageUpdate);

        return () => {
            tokenUsageTracker.off("usageUpdated", handleUsageUpdate);
        };
    }, []);

    if (!usageData) {
        return (
            <div
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${className}`}
            >
                <div className="flex items-center gap-2 mb-3">
                    <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                    </svg>
                    <h3 className="text-sm font-medium text-gray-900">
                        Token Usage
                    </h3>
                </div>
                <div className="space-y-2">
                    <p className="text-sm text-gray-500">
                        Loading usage data...
                    </p>
                    <p className="text-xs text-gray-400">
                        Make sure you have configured your Passio API key in
                        your profile settings.
                    </p>
                </div>
            </div>
        );
    }

    const percentage = tokenUsageTracker.getUsagePercentage();
    const remaining = tokenUsageTracker.getRemainingTokens();
    const isHighUsage = percentage > 80;
    const isCritical = percentage > 90;

    const formatNumber = (num: number): string => {
        if (num >= 1000000) {
            return `${(num / 1000000).toFixed(2)}M`;
        } else if (num >= 1000) {
            return `${(num / 1000).toFixed(1)}K`;
        }
        return num.toString();
    };

    const getProgressColor = () => {
        if (isCritical) return "bg-red-500";
        if (isHighUsage) return "bg-orange-500";
        return "bg-blue-500";
    };

    const getTextColor = () => {
        if (isCritical) return "text-red-600";
        if (isHighUsage) return "text-orange-600";
        return "text-gray-900";
    };

    return (
        <div
            className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${className}`}
        >
            <div className="flex items-center gap-2 mb-3">
                <svg
                    className="w-4 h-4 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                </svg>
                <h3 className="text-sm font-medium text-gray-900">
                    Token Usage
                </h3>
                {isCritical && (
                    <svg
                        className="w-4 h-4 text-red-500 animate-pulse"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                )}
            </div>

            <div className="space-y-3">
                <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                            {formatNumber(usageData.periodUsage)} /{" "}
                            {formatNumber(usageData.budgetCap)}
                        </span>
                        <span className={`font-medium ${getTextColor()}`}>
                            {percentage.toFixed(1)}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                        />
                    </div>
                </div>

                {showDetails && (
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span className="text-gray-500">Remaining</span>
                            <span className="font-medium text-gray-900">
                                {formatNumber(remaining)} tokens
                            </span>
                        </div>
                        {usageData.resetTime && (
                            <div className="flex justify-between">
                                <span className="text-gray-500">Next Billing Date (And Token Reset)</span>
                                <span className="font-medium text-gray-900">
                                    {new Date(
                                        usageData.resetTime
                                    ).toLocaleDateString()}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
